import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarDays } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { createAvailability } from "../../redux/availabilitySlice";

const AvailabilityModal = ({ isOpen, onClose, date, onSave, disabledSlots = [], preSelectedSlots = [] }) => {
  const { t } = useTranslation();
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  const dispatch = useDispatch();
  const therapist = useSelector((state) => state.therapist.therapist);
  const availabilityError = useSelector((state) => state.availability?.error);

  if (!isOpen) return null;

  const toggleSlot = (slot) => {
    const isDisabled = disabledSlots.some(
      (ds) => ds.startHour === slot.startHour && ds.endHour === slot.endHour
    );
    if (isDisabled) {
      return;
    }
    const exists = selectedSlots.some(
      (s) => s.startHour === slot.startHour && s.endHour === slot.endHour
    );
    if (exists) {
      setSelectedSlots(selectedSlots.filter(
        (s) => !(s.startHour === slot.startHour && s.endHour === slot.endHour)
      ));
    } else {
      setSelectedSlots([...selectedSlots, slot]);
    }
  };

  // Yerel saat diliminde tarih oluşturma fonksiyonu
  const createLocalDateTime = (year, month, day, hour, minute = 0) => {
    // Yerel saat formatında string oluştur
    const dateStr = `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;
    return new Date(dateStr);
  };

  const handleSaveAll = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (selectedSlots.length === 0) {
      setError(t("pleaseSelectAtLeastOneSlot"));
      setLoading(false);
      return;
    }

    try {
      for (const slot of selectedSlots) {
        // Yerel saat diliminde tarih oluştur
        const startDate = createLocalDateTime(date.year, date.month, date.day, slot.startHour, 0);
        const endDate = createLocalDateTime(date.year, date.month, date.day, slot.endHour, 0);
        
        const priceNote = "Ücretsiz seans";

        const availabilityData = {
          therapistId: String(therapist._id),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          status: "available",
          notes: priceNote
        };

        console.log("Gönderilen veri:", {
          secilenSaat: `${slot.startHour}:00 - ${slot.endHour}:00`,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          yerelSaat: `${startDate.toLocaleString('en-GB')} - ${endDate.toLocaleString('en-GB')}`
        });

        const resultAction = await dispatch(createAvailability(availabilityData));
        if (!createAvailability.fulfilled.match(resultAction)) {
          setError(resultAction.payload || t("errorCreatingAvailability"));
          setLoading(false);
          return;
        }
      }

      onSave(selectedSlots);
      setSelectedSlots([]);
      setError("");
    } catch (err) {
      setError(err.message || t("errorCreatingAvailability"));
    } finally {
      setLoading(false);
    }
  };

  const dateFormatted = `${date.day} ${t(
    `${new Date(date.year, date.month).toLocaleString("en-US", {
      month: "long",
    })}`
  )} ${date.year}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl w-full max-w-md p-5 shadow-2xl transition-shadow duration-200">
        <div className="flex items-center justify-center gap-2 border-b pb-2 mb-4">
          <CalendarDays size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-primary">{t("addAvailabilitySlot")}</h2>
        </div>

        <div className="flex items-center justify-center gap-2 px-3 py-2 mb-4 bg-tertiary text-text rounded-lg border">
          <CalendarDays size={16} />
          <span className="text-sm font-medium">{dateFormatted}</span>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border shadow-sm p-3 rounded-lg text-red-800 transition duration-150">
            <p>{error}</p>
          </div>
        )}
        
        {availabilityError && !error && (
          <div className="mb-4 bg-red-50 border shadow-sm p-3 rounded-lg text-red-800 transition duration-150">
            <p>{availabilityError}</p>
          </div>
        )}

        <form onSubmit={handleSaveAll}>
          <div className="mb-4 grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto p-2 bg-gray-50 rounded shadow-inner transition-shadow duration-200">
            {Array.from({ length: 18 }, (_, i) => {
              const hour = i + 6;
              const slot = { startHour: hour, endHour: hour + 1 };
              const isSelected = selectedSlots.some(
                (s) => s.startHour === slot.startHour && s.endHour === slot.endHour
              ) || preSelectedSlots.some(
                (s) => s.startHour === slot.startHour && s.endHour === slot.endHour
              );
              const isDisabled = loading || disabledSlots.some(
                (ds) => ds.startHour === slot.startHour && ds.endHour === slot.endHour
              ) || preSelectedSlots.some(
                (ps) => ps.startHour === slot.startHour && ps.endHour === slot.endHour
              );
              const label = `${hour.toString().padStart(2, "0")}:00 - ${(hour + 1).toString().padStart(2, "0")}:00`;
              return (
                <button
                  key={`slot-${hour}`}
                  type="button"
                  onClick={() => toggleSlot(slot)}
                  disabled={isDisabled}
                  className={`text-sm rounded-lg py-2 px-3 shadow-md transition 
                    ${isSelected && isDisabled ? "bg-gray-300 text-gray-500 cursor-not-allowed" :
                    isSelected ? "bg-button-bg text-white shadow-inner" : 
                    "bg-white text-gray-700 border hover:bg-gray-100"}`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="flex justify-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-all duration-150 shadow-sm"
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              disabled={loading || selectedSlots.length === 0}
              className="px-4 py-2 bg-button-bg hover:bg-primary-dark transition-all duration-150 text-white rounded-lg flex items-center justify-center shadow-sm disabled:opacity-50"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                  {t("saving")}...
                </>
              ) : (
                t("save")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AvailabilityModal;